import { useEffect, useRef, useState } from 'react'
import { Company } from '../types'

interface FilterBarProps {
  productMode: 'stories' | 'live'
  onProductModeChange: (v: 'stories' | 'live') => void
  segments: Array<{ name: string; count: number }>
  plans: Array<{ name: string; count: number }>
  filters: { segment: string | null; planType: string | null }
  onFilterChange: (key: 'segment' | 'planType', value: string | null) => void
  onClearFilters: () => void
  selectedCompany: Company | null
  onSelectCompany: (company: Company | null) => void
  searchCompanies: (query: string) => Company[]
  isLoading?: boolean
}

type ActiveCategory = 'product' | 'segment' | 'plan' | null

// Search icon SVG
function SearchIcon() {
  return (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  )
}

// Building icon for company tag
function BuildingIcon() {
  return (
    <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  )
}

// Chevron right
function ChevronRight() {
  return (
    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 18l6-6-6-6" />
    </svg>
  )
}

// Chevron left (back)
function ChevronLeft() {
  return (
    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 18l-6-6 6-6" />
    </svg>
  )
}

// Checkmark
function CheckIcon() {
  return (
    <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
    </svg>
  )
}

// Play icon (Video Commerce)
function PlayIcon({ size = 11 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  )
}

// Live (radio waves) icon (Live Commerce)
function LiveIcon({ size = 11 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="2.5" fill="currentColor" />
      <path d="M5.5 12a6.5 6.5 0 0 1 13 0" />
      <path d="M2 12a10 10 0 0 1 20 0" />
    </svg>
  )
}

export function FilterBar({
  productMode,
  onProductModeChange,
  segments,
  plans,
  filters,
  onFilterChange,
  onClearFilters,
  selectedCompany,
  onSelectCompany,
  searchCompanies,
  isLoading = false,
}: FilterBarProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeCategory, setActiveCategory] = useState<ActiveCategory>(null)
  const [query, setQuery] = useState('')
  const [companyResults, setCompanyResults] = useState<Company[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Compute active tags
  const hasAnyFilter =
    productMode === 'live' ||
    filters.segment !== null ||
    filters.planType !== null ||
    selectedCompany !== null ||
    query.trim() !== ''

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setActiveCategory(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        setActiveCategory(null)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Update company search results when query changes
  useEffect(() => {
    if (query.trim()) {
      setCompanyResults(searchCompanies(query).slice(0, 8))
    } else {
      setCompanyResults([])
    }
  }, [query, searchCompanies])

  const handleBarClick = () => {
    setIsOpen(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    onProductModeChange('stories')
    onClearFilters()
    onSelectCompany(null)
    setQuery('')
    setIsOpen(false)
    setActiveCategory(null)
  }

  const handleSelectCompany = (company: Company) => {
    onSelectCompany(company)
    setQuery('')
    setCompanyResults([])
    setIsOpen(false)
    setActiveCategory(null)
  }

  // Select a filter value and go back to root (stay open)
  const handleSelectValue = (key: 'segment' | 'planType', value: string, currentValue: string | null) => {
    onFilterChange(key, currentValue === value ? null : value)
    setActiveCategory(null)
  }

  const handleSelectProductMode = (mode: 'stories' | 'live') => {
    onProductModeChange(mode)
    setActiveCategory(null)
  }

  // Category label mapping
  const categoryLabel = (cat: NonNullable<ActiveCategory>): string => {
    const labels: Record<NonNullable<ActiveCategory>, string> = {
      product: 'Produto',
      segment: 'Segmento',
      plan: 'Plano',
    }
    return labels[cat]
  }

  return (
    <div className="filter-bar-wrap" ref={containerRef}>
      {/* The bar itself */}
      <div
        className={`filter-bar ${isOpen ? 'open' : ''}`}
        onClick={handleBarClick}
      >
        <span className="filter-bar-search-icon">
          <SearchIcon />
        </span>

        {/* Product tag — always visible */}
        {productMode === 'stories' ? (
          <span className="filter-tag" style={{ flexShrink: 0 }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            <span>Video Commerce</span>
          </span>
        ) : (
          <span className="filter-tag" style={{ background: '#fff4ec', borderColor: '#fecdab', color: '#c4400f', flexShrink: 0 }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="2.5" fill="currentColor"/><path d="M5.5 12a6.5 6.5 0 0 1 13 0"/></svg>
            <span>Live Commerce</span>
            <button
              className="filter-tag-remove"
              onClick={e => { e.stopPropagation(); onProductModeChange('stories') }}
              title="Voltar para Video Commerce"
            >
              ×
            </button>
          </span>
        )}

        {filters.segment && (
          <span className="filter-tag">
            <span>Segmento: {filters.segment}</span>
            <button
              className="filter-tag-remove"
              onClick={e => { e.stopPropagation(); onFilterChange('segment', null) }}
              title="Remover filtro Segmento"
            >
              ×
            </button>
          </span>
        )}

        {filters.planType && (
          <span className="filter-tag">
            <span>Plano: {filters.planType}</span>
            <button
              className="filter-tag-remove"
              onClick={e => { e.stopPropagation(); onFilterChange('planType', null) }}
              title="Remover filtro Plano"
            >
              ×
            </button>
          </span>
        )}

        {selectedCompany && (
          <span className="filter-tag" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d' }}>
            <BuildingIcon />
            <span>{selectedCompany.title}</span>
            <button
              className="filter-tag-remove"
              onClick={e => { e.stopPropagation(); onSelectCompany(null) }}
              title="Remover empresa selecionada"
            >
              ×
            </button>
          </span>
        )}

        {/* Text input — hidden when a company is selected */}
        {!selectedCompany && (
          <input
            ref={inputRef}
            className="filter-bar-text"
            placeholder={isLoading ? 'Carregando...' : productMode === 'live' ? 'Buscar empresa para Live Commerce…' : 'Filtrar...'}
            value={query}
            disabled={isLoading}
            onChange={e => setQuery(e.target.value)}
            onClick={e => e.stopPropagation()}
            onFocus={() => setIsOpen(true)}
            autoComplete="off"
          />
        )}

        {/* Spacer */}
        <div style={{ flex: 1, minWidth: 4 }} />

        {/* Clear all button */}
        {hasAnyFilter && (
          <button
            className="filter-bar-clear"
            onClick={handleClearAll}
            title="Limpar todos os filtros"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="filter-bar-dropdown">
          {activeCategory === null ? (
            /* Root panel */
            <>
              <div className="filter-bar-hint">Filtrar por</div>

              {/* Produto */}
              <button
                className="filter-bar-row"
                onClick={() => setActiveCategory('product')}
                style={{ alignItems: 'center', gap: 8 }}
              >
                <span style={{
                  width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                  background: productMode === 'live' ? 'var(--accent-coral, #f97316)' : 'var(--brand-500)',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {productMode === 'live' ? <LiveIcon /> : <PlayIcon />}
                </span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: 13 }}>
                  {productMode === 'live' ? 'Live Commerce' : 'Video Commerce'}
                </span>
                <ChevronRight />
              </button>

              {/* Only show group filters when NOT in live mode */}
              {productMode !== 'live' && (
                <>
                  {/* Segmento */}
                  <button
                    className="filter-bar-row"
                    onClick={() => setActiveCategory('segment')}
                  >
                    <span style={{ flex: 1 }}>Segmento</span>
                    {filters.segment && (
                      <span className="filter-bar-row-muted">{filters.segment}</span>
                    )}
                    <ChevronRight />
                  </button>

                  {/* Plano */}
                  <button
                    className="filter-bar-row"
                    onClick={() => setActiveCategory('plan')}
                  >
                    <span style={{ flex: 1 }}>Plano</span>
                    {filters.planType && (
                      <span className="filter-bar-row-muted">{filters.planType}</span>
                    )}
                    <ChevronRight />
                  </button>

                </>
              )}

              {/* Company search results */}
              {query.trim() && (
                <>
                  <div className="filter-bar-divider" />
                  <div className="filter-bar-hint">Empresas</div>
                  {companyResults.length > 0 ? (
                    companyResults.map(company => (
                      <button
                        key={company.key}
                        className="filter-bar-row"
                        onClick={() => handleSelectCompany(company)}
                      >
                        <BuildingIcon />
                        <span style={{ flex: 1 }}>{company.title}</span>
                        {company.segment && (
                          <span className="filter-bar-row-count">{company.segment}</span>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="filter-bar-empty">Nenhuma empresa encontrada</div>
                  )}
                </>
              )}
            </>
          ) : (
            /* Values panel */
            <>
              <button
                className="filter-bar-back"
                onClick={() => setActiveCategory(null)}
              >
                <ChevronLeft />
                {categoryLabel(activeCategory)}
              </button>
              <div className="filter-bar-divider" />

              {activeCategory === 'product' && (
                <div style={{ padding: '4px 6px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {/* Video Commerce option */}
                  <button
                    onClick={() => handleSelectProductMode('stories')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      fontFamily: 'inherit', textAlign: 'left', width: '100%',
                      background: productMode === 'stories' ? 'var(--brand-50, #f5f3ff)' : 'transparent',
                      outline: productMode === 'stories' ? '1.5px solid var(--brand-200, #ddd6fe)' : 'none',
                      transition: 'background 0.12s',
                    }}
                  >
                    <span style={{
                      width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                      background: 'var(--brand-500)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <PlayIcon size={13} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: productMode === 'stories' ? 'var(--brand-700)' : 'var(--ink-800, #1f2937)', lineHeight: 1.2 }}>
                        Video Commerce
                      </span>
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-500)', marginTop: 1 }}>
                        Atribuição por stories e vídeos
                      </span>
                    </span>
                    {productMode === 'stories' && (
                      <span style={{ color: 'var(--brand-600)', flexShrink: 0 }}><CheckIcon /></span>
                    )}
                  </button>

                  {/* Live Commerce option */}
                  <button
                    onClick={() => handleSelectProductMode('live')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      fontFamily: 'inherit', textAlign: 'left', width: '100%',
                      background: productMode === 'live' ? '#fff5f0' : 'transparent',
                      outline: productMode === 'live' ? '1.5px solid #fecdab' : 'none',
                      transition: 'background 0.12s',
                    }}
                  >
                    <span style={{
                      width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                      background: 'var(--accent-coral, #f97316)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <LiveIcon size={13} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: productMode === 'live' ? '#c2410c' : 'var(--ink-800, #1f2937)', lineHeight: 1.2 }}>
                        Live Commerce
                      </span>
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-500)', marginTop: 1 }}>
                        Atribuição por transmissões ao vivo
                      </span>
                    </span>
                    {productMode === 'live' && (
                      <span style={{ color: '#c2410c', flexShrink: 0 }}><CheckIcon /></span>
                    )}
                  </button>
                </div>
              )}

              {activeCategory === 'segment' && (
                segments.length > 0 ? segments.map(opt => (
                  <button
                    key={opt.name}
                    className={`filter-bar-row ${filters.segment === opt.name ? 'selected' : ''}`}
                    onClick={() => handleSelectValue('segment', opt.name, filters.segment)}
                  >
                    <span style={{ flex: 1 }}>{opt.name}</span>
                    <span className="filter-bar-row-count">{opt.count}</span>
                    {filters.segment === opt.name && <CheckIcon />}
                  </button>
                )) : (
                  <div className="filter-bar-empty">Nenhuma opção disponível</div>
                )
              )}

              {activeCategory === 'plan' && (
                plans.length > 0 ? plans.map(opt => (
                  <button
                    key={opt.name}
                    className={`filter-bar-row ${filters.planType === opt.name ? 'selected' : ''}`}
                    onClick={() => handleSelectValue('planType', opt.name, filters.planType)}
                  >
                    <span style={{ flex: 1 }}>{opt.name}</span>
                    <span className="filter-bar-row-count">{opt.count}</span>
                    {filters.planType === opt.name && <CheckIcon />}
                  </button>
                )) : (
                  <div className="filter-bar-empty">Nenhuma opção disponível</div>
                )
              )}


            </>
          )}
        </div>
      )}
    </div>
  )
}
